import React, { useEffect, useState, useCallback } from 'react';
import { Bell, Mail, MailOpen, MousePointerClick, AlertTriangle, Loader2, Send, BanIcon } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

const STATUS_PILL = {
  sent: 'bg-green-100 text-green-700',
  queued: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  suppressed: 'bg-gray-100 text-gray-600',
};

const TYPE_LABEL = {
  new_listing_matched: 'Matched',
  new_listing_general: 'General',
  price_change: 'Price change',
  offer_update: 'Offer update',
  weekly_digest: 'Digest',
  reengagement: 'Re-engagement',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function StatCard({ icon: Icon, label, value, accent = 'text-[#0a3d62]' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4 flex items-center gap-3">
      <Icon className={accent} size={22} />
      <div>
        <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
        <div className="text-xl font-bold text-gray-900">{value}</div>
      </div>
    </div>
  );
}

export default function PropertyNotificationsTab({ propertyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [togglingSuppress, setTogglingSuppress] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/notifications/listings/${propertyId}/log/`);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load notifications log');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (propertyId) load();
  }, [propertyId, load]);

  const toggleSuppress = async () => {
    if (!data) return;
    setTogglingSuppress(true);
    try {
      const res = await api.post(`/notifications/listings/${propertyId}/suppress/`, {
        suppress: !data.suppress_notifications,
      });
      setData((d) => ({ ...d, suppress_notifications: res.data.suppress_notifications }));
      toast.success(res.data.suppress_notifications
        ? 'Notifications suppressed for this listing'
        : 'Notifications re-enabled');
    } catch {
      toast.error('Failed to update suppression');
    } finally {
      setTogglingSuppress(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-gray-500">
        <Loader2 className="animate-spin mr-2" /> Loading notifications log…
      </div>
    );
  }
  if (!data) return null;

  const { aggregate, rows, suppress_notifications } = data;
  const sent = aggregate.sent || 0;
  const openRate = sent ? Math.round((aggregate.opened / sent) * 100) : 0;
  const clickRate = sent ? Math.round((aggregate.clicked / sent) * 100) : 0;

  return (
    <div className="animate-in zoom-in-95 duration-500 space-y-6 pt-6">
      {/* Suppress banner */}
      <div className={`rounded-md border p-4 flex items-center justify-between ${suppress_notifications ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center gap-3">
          {suppress_notifications ? <BanIcon className="text-amber-600" /> : <Bell className="text-[#0a3d62]" />}
          <div>
            <div className="font-semibold text-gray-900">
              {suppress_notifications ? 'Notifications suppressed' : 'Notifications active'}
            </div>
            <div className="text-sm text-gray-600">
              {suppress_notifications
                ? 'Buyer Portal emails will NOT be sent for this listing (publish, price change, digest).'
                : 'Buyer Portal emails fire automatically when this listing publishes or its price changes.'}
            </div>
          </div>
        </div>
        <button
          onClick={toggleSuppress}
          disabled={togglingSuppress}
          className={`px-4 py-2 rounded-md text-sm font-semibold border transition
            ${suppress_notifications
              ? 'bg-[#0a3d62] text-white border-[#0a3d62] hover:bg-[#0c4d7a]'
              : 'bg-white text-amber-700 border-amber-400 hover:bg-amber-50'}`}
        >
          {togglingSuppress ? 'Saving…' : suppress_notifications ? 'Re-enable' : 'Suppress'}
        </button>
      </div>

      {/* Aggregate stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Mail} label="Sent" value={sent} />
        <StatCard icon={MailOpen} label="Opened" value={`${aggregate.opened} (${openRate}%)`} />
        <StatCard icon={MousePointerClick} label="Clicked" value={`${aggregate.clicked} (${clickRate}%)`} />
        <StatCard icon={Send} label="Queued" value={aggregate.queued} accent="text-yellow-600" />
        <StatCard icon={AlertTriangle} label="Failed" value={aggregate.failed} accent="text-red-500" />
      </div>

      {/* Rows */}
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Recipients</h3>
          <span className="text-xs text-gray-500">Showing latest {rows.length}</span>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No notifications sent for this listing yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Recipient</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Sent</th>
                  <th className="px-4 py-3 text-left">Opened</th>
                  <th className="px-4 py-3 text-left">Clicked</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.recipient_name || r.recipient_email}</div>
                      <div className="text-xs text-gray-500">{r.recipient_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold
                        ${r.is_matched ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                        {TYPE_LABEL[r.notification_type] || r.notification_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold uppercase ${STATUS_PILL[r.status] || 'bg-gray-100 text-gray-600'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(r.sent_at)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(r.opened_at)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(r.clicked_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
